import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../../context/AuthContext";
import { oneKlicks } from "../../../../SDK/oneclick";
import ImageShow from "../../../../Uitiles/ImageShow";
import { Badge, Modal, Button } from "react-bootstrap";

const initialInsuranceSummary = {
    totalPolicies: 0,
    activePolicies: 0,
    totalCustomers: 0,
    totalBeneficiaries: 0,
    partnerCompanies: 0,
    totalAgents: 0,
    totalLeads: 0,
    openLeads: 0,
    hotLeads: 0,
    premiumCollected: 0,
    averagePremium: 0,
};

const normaliseRows = (response) => {
    if (!response?.success) {
        return [];
    }
    if (Array.isArray(response.data)) {
        return response.data;
    }
    if (Array.isArray(response.data?.items)) {
        return response.data.items;
    }
    return [];
};

const formatCurrency = (value) => {
    if (!value) {
        return "₹0";
    }
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
    }).format(value);
};

const formatActivityType = (type) => {
    switch ((type || "").toLowerCase()) {
        case "policy":
            return "Policy";
        case "customer":
            return "Customer";
        case "beneficiary":
            return "Beneficiary";
        case "agent":
            return "Agent";
        case "premium":
            return "Premium";
        default:
            return "Update";
    }
};

const parseMetaPayload = (metaValue) => {
    if (!metaValue) {
        return {};
    }
    if (typeof metaValue === "object") {
        return metaValue || {};
    }
    try {
        return JSON.parse(metaValue);
    } catch (error) {
        console.warn("Failed to parse meta payload", error);
        return {};
    }
};

const toNumericOrNull = (value) => {
    if (value === undefined || value === null || value === "") {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const recordMatchesInsuranceUser = (record, userData, entity) => {
    if (!record || !userData?.id) {
        return false;
    }

    const userId = toNumericOrNull(userData.id);
    const companyId = toNumericOrNull(userData.company_id || userData.companyId || userData.company);
    const agentId = toNumericOrNull(userData.agent_id || userData.agentId);

    const recordCompanyId = toNumericOrNull(record.company_id || record.companyId);
    const recordAgentId = toNumericOrNull(record.agent_id || record.agentId);

    if (companyId && recordCompanyId && companyId === recordCompanyId) {
        return true;
    }

    if (agentId && recordAgentId && agentId === recordAgentId) {
        return true;
    }

    const directUserCandidates = [
        record.user_id,
        record.userId,
        record.created_by,
        record.createdBy,
        record.owner_id,
        record.ownerId,
        record.agent_user_id,
    ]
        .map(toNumericOrNull)
        .filter((value) => value !== null);

    if (directUserCandidates.includes(userId)) {
        return true;
    }

    const meta = parseMetaPayload(record.meta);
    const metaCandidates = [
        meta?.created_by,
        meta?.user_id,
        meta?.owner_id,
        meta?.agent_id,
    ]
        .map(toNumericOrNull)
        .filter((value) => value !== null);

    if (metaCandidates.includes(userId)) {
        return true;
    }

    if (entity === "company" && companyId && toNumericOrNull(record.id) === companyId) {
        return true;
    }

    return false;
};

const parseDate = (value) => {
    if (!value) return new Date(0);
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
        return date;
    }
    // try parsing YYYY-MM-DD style strings
    if (typeof value === "string" && value.includes("-")) {
        const parts = value.split(" ")[0].split("-");
        if (parts.length === 3) {
            const parsed = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
            if (!Number.isNaN(parsed.getTime())) {
                return parsed;
            }
        }
    }
    return new Date(0);
};

const formatTimeAgo = (date) => {
    if (!(date instanceof Date)) {
        return "Just now";
    }
    const diffMs = Date.now() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    if (diffMinutes <= 0) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const CardViews = () => {
    const { userData } = useAuth();

    // Initialize role from userData if available, otherwise default to empty string
    const getInitialRole = () => {
        if (userData?.user_type) {
            return userData.user_type.toString().toLowerCase();
        }
        return "";
    };

    const [role, setRole] = useState(getInitialRole());
    const [metrics, setMetrics] = useState({});
    const [appointmentCounts, setAppointmentCounts] = useState({
        total: 0,
        upcoming: 0,
        completed: 0,
        cancelled: 0,
        recentPayments: 0,
        totalRevenue: 0,
        amount: "₹0"
    });
    const [recentAppointments, setRecentAppointments] = useState([]);
    const [loadingAppointments, setLoadingAppointments] = useState(false);
    const [recentClaims, setRecentClaims] = useState([]);
    const [loadingClaims, setLoadingClaims] = useState(false);
    const [insuranceSummary, setInsuranceSummary] = useState(initialInsuranceSummary);
    const [recentInsuranceActivities, setRecentInsuranceActivities] = useState([]);
    const [loadingInsuranceActivity, setLoadingInsuranceActivity] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [showAppointmentDetailModal, setShowAppointmentDetailModal] = useState(false);

    const insuranceActivityMeta = useMemo(
        () => ({
            policy: { icon: "ti-file", color: "#2563eb" },
            customer: { icon: "ti-user", color: "#059669" },
            beneficiary: { icon: "ti-heart", color: "#ec4899" },
            agent: { icon: "ti-id-badge", color: "#f97316" },
            premium: { icon: "ti-wallet", color: "#7c3aed" },
            lead: { icon: "ti-target", color: "#3b82f6" },
            default: { icon: "ti-timer", color: "#0ea5e9" },
        }),
        []
    );

    const handleViewAppointment = (apt) => {
        setSelectedAppointment(apt);
        setShowAppointmentDetailModal(true);
    };

    const handleResend = (e) => {
        e.preventDefault();
        alert("Resend email clicked!");
    };

    const fetchInsuranceSummary = async () => {
        if (!userData?.id) {
            setInsuranceSummary(initialInsuranceSummary);
            setRecentInsuranceActivities([]);
            return;
        }

        try {
            setLoadingInsuranceActivity(true);

            const filterRows = (rows, entity) => {
                return rows.filter((row) => recordMatchesInsuranceUser(row, userData, entity));
            };

            const policyConditions = {};
            if (userData?.company_id) {
                policyConditions.company_id = userData.company_id;
            }
            if (userData?.agent_id) {
                policyConditions.agent_id = userData.agent_id;
            }
            if (!policyConditions.company_id && !policyConditions.agent_id) {
                policyConditions.user_id = userData.id;
            }

            const customerConditions = { ...policyConditions };

            const beneficiaryConditions = {};
            if (userData?.company_id) {
                beneficiaryConditions.company_id = userData.company_id;
            } else if (userData?.id) {
                beneficiaryConditions.user_id = userData.id;
            }

            const companyConditions = {};
            if (userData?.company_id) {
                companyConditions.id = userData.company_id;
            }

            const agentConditions = {};
            if (userData?.company_id) {
                agentConditions.company_id = userData.company_id;
            }
            if (userData?.agent_id) {
                agentConditions.id = userData.agent_id;
            }

            const [policiesRes, customersRes, beneficiariesRes, companiesRes, agentsRes, leadRes] = await Promise.all([
                oneKlicks.read("", "insurance_policies", policyConditions, "id DESC", "", "", "0,500"),
                oneKlicks.read("", "insurance_customers", customerConditions, "id DESC", "", "", "0,500"),
                oneKlicks.read("", "insurance_beneficiaries", beneficiaryConditions, "id DESC", "", "", "0,500"),
                oneKlicks.read("", "insurance_companies", companyConditions, "id DESC", "", "", "0,100"),
                oneKlicks.read("", "insurance_agents", agentConditions, "id DESC", "", "", "0,200"),
                oneKlicks.read("", "insurance_leads", {}, "id DESC", "", "", "0,500"),
            ]);

            const policyRows = filterRows(normaliseRows(policiesRes), "policy");
            const customerRows = filterRows(normaliseRows(customersRes), "customer");
            const beneficiaryRows = filterRows(normaliseRows(beneficiariesRes), "beneficiary");
            const companyRows = filterRows(normaliseRows(companiesRes), "company");
            const agentRows = filterRows(normaliseRows(agentsRes), "agent");
            const leadRows = filterRows(normaliseRows(leadRes), "lead");

            const totalPolicies = policyRows.length;
            const activePolicies = policyRows.filter((policy) => (policy.status || "").toLowerCase() === "active").length;

            const premiumCollected = policyRows.reduce((sum, policy) => sum + (Number(policy.premium_amount) || 0), 0);
            const averagePremium = totalPolicies ? premiumCollected / totalPolicies : 0;

            const partnerCompanyIds = new Set();
            policyRows.forEach((policy) => {
                if (policy.company_id) {
                    partnerCompanyIds.add(policy.company_id);
                }
            });
            companyRows.forEach((company) => {
                if (company?.id) {
                    partnerCompanyIds.add(company.id);
                }
            });
            if (userData?.company_id) {
                partnerCompanyIds.add(userData.company_id);
            }

            const totalAgents = agentRows.length || (userData?.agent_id ? 1 : 0);

            setInsuranceSummary({
                totalPolicies,
                activePolicies,
                totalCustomers: customerRows.length,
                totalBeneficiaries: beneficiaryRows.length,
                partnerCompanies: partnerCompanyIds.size,
                totalAgents,
                premiumCollected,
                averagePremium,
                totalLeads: leadRows.length,
                openLeads: leadRows.filter((lead) => (lead.status || "").toLowerCase() === "open").length,
                hotLeads: leadRows.filter((lead) => (lead.priority || "").toLowerCase() === "hot").length,
            });

            const activities = [];

            policyRows.slice(0, 5).forEach((policy) => {
                activities.push({
                    type: "policy",
                    title: policy.policy_number ? `Policy ${policy.policy_number}` : "New Policy",
                    description: policy.status ? `Status: ${policy.status}` : "Policy created",
                    timestamp: parseDate(policy.created_at || policy.updated_at || policy.issue_date),
                    link: "/insurance/policies",
                });
            });

            customerRows.slice(0, 5).forEach((customer) => {
                activities.push({
                    type: "customer",
                    title: customer.name || customer.full_name || `Customer #${customer.id}`,
                    description: customer.relationship ? `Relationship: ${customer.relationship}` : "Customer added",
                    timestamp: parseDate(customer.created_at || customer.updated_at),
                    link: "/insurance/customers",
                });
            });

            beneficiaryRows.slice(0, 5).forEach((beneficiary) => {
                activities.push({
                    type: "beneficiary",
                    title: beneficiary.name || `Beneficiary #${beneficiary.id}`,
                    description: beneficiary.relationship ? `Relationship: ${beneficiary.relationship}` : "Beneficiary added",
                    timestamp: parseDate(beneficiary.created_at || beneficiary.updated_at),
                    link: "/insurance/beneficiaries/new",
                });
            });

            agentRows.slice(0, 5).forEach((agent) => {
                activities.push({
                    type: "agent",
                    title: agent.name || agent.full_name || `Agent #${agent.id}`,
                    description: agent.agent_code ? `Agent code: ${agent.agent_code}` : "Agent onboarded",
                    timestamp: parseDate(agent.created_at || agent.updated_at),
                    link: "/insurance/agents/new",
                });
            });

            leadRows.slice(0, 5).forEach((lead) => {
                const status = (lead.status || lead.pipeline_stage || "").replace(/_/g, " ");
                const priority = (lead.priority || lead.lead_priority || "").replace(/_/g, " ");
                activities.push({
                    type: "lead",
                    title: lead.lead_name || lead.name || lead.contact_name || `Lead #${lead.id}`,
                    description: [status ? `Status: ${status}` : null, priority ? `Priority: ${priority}` : null]
                        .filter(Boolean)
                        .join(" • ") || "Lead updated",
                    timestamp: parseDate(lead.follow_up_date || lead.updated_at || lead.created_at),
                    link: "/insurance/leads",
                });
            });

            policyRows.slice(0, 5).forEach((policy) => {
                activities.push({
                    type: "premium",
                    title: "Premium Recorded",
                    description: formatCurrency(Number(policy.premium_amount) || 0),
                    timestamp: parseDate(policy.premium_due_date || policy.created_at),
                    link: "/insurance/analytics",
                });
            });

            activities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
            setRecentInsuranceActivities(activities.slice(0, 12));
        } catch (error) {
            console.error("Error fetching insurance summary:", error);
            setInsuranceSummary(initialInsuranceSummary);
            setRecentInsuranceActivities([]);
        } finally {
            setLoadingInsuranceActivity(false);
        }
    };

    const fetchAppointmentCounts = async () => {
        try {
            const userRole = role.toLowerCase().trim();
            const isDoctor = userRole === "doctor";
            const conditions = isDoctor ? { doctor_id: userData.id } : (userData?.id ? { patient_id: userData.id } : { patient_phone: userData?.phone });

            const response = await oneKlicks.read("", "appointments", conditions, "id DESC", "", "", "");

            if (response?.success && response?.data) {
                const appointments = response.data;
                const total = appointments.length;
                let totalRevenue = 0;
                let recentPaymentsCount = 0;

                const upcoming = appointments.filter((apt) => {
                    const status = (apt.status || "").toLowerCase();
                    const aptDate = new Date(apt.appointment_date);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return (status === "confirmed" || status === "pending" || status === "upcoming") && aptDate >= today;
                }).length;

                const completed = appointments.filter((apt) => {
                    const status = (apt.status || "").toLowerCase();
                    return status === "completed" || status === "done" || status === "finished";
                }).length;

                // Calculate Revenue and Recent Payments count for ALL appointments that have an amount
                appointments.forEach(apt => {
                    const amount = Number(apt.amount || apt.display_amount) || 0;
                    if (amount > 0) {
                        totalRevenue += amount;
                        recentPaymentsCount++;
                    }
                });

                const cancelled = appointments.filter((apt) => {
                    const status = (apt.status || "").toLowerCase();
                    return status === "cancelled" || status === "canceled";
                }).length;

                setAppointmentCounts({
                    total,
                    upcoming,
                    completed,
                    cancelled,
                    recentPayments: recentPaymentsCount,
                    totalRevenue,
                    amount: formatCurrency(totalRevenue),
                });
            }
        } catch (error) {
            console.error("Error fetching appointment counts:", error);
        }
    };

    const fetchRecentAppointments = async () => {
        try {
            setLoadingAppointments(true);
            const userRole = role.toLowerCase().trim();
            const isDoctor = userRole === "doctor";

            let conditions = {};
            let margedata = [];

            if (isDoctor) {
                conditions = { doctor_id: userData.id };
                margedata = [
                    {
                        target_table: 'users',
                        target_column: 'id',
                        target_value: 'patient_id',
                        target_label: 'patient'
                    }
                ];
            } else {
                conditions = userData?.id ? { patient_id: userData.id } : { patient_phone: userData?.phone };
                margedata = [
                    {
                        target_table: 'users',
                        target_column: 'id',
                        target_value: 'doctor_id',
                        target_label: 'doctor'
                    }
                ];
            }

            const response = await oneKlicks.read("", "appointments", conditions, "id DESC", "", { page: 1, limit: 8 }, margedata);

            if (response?.success && response?.data) {
                setRecentAppointments(response.data);
            } else {
                setRecentAppointments([]);
            }
        } catch (error) {
            console.error("Error fetching recent appointments:", error);
            setRecentAppointments([]);
        } finally {
            setLoadingAppointments(false);
        }
    };

    const fetchRecentClaims = async () => {
        try {
            setLoadingClaims(true);
            const conditions = {};

            const companyId = userData?.company_id || userData?.companyId || userData?.company;
            if (companyId) {
                conditions.company_id = companyId;
            }

            const agentId = userData?.agent_id || userData?.agentId;
            if (!conditions.company_id && agentId) {
                conditions.agent_id = agentId;
            }

            const response = await oneKlicks.read("", "insurance_claims", conditions, "created_at DESC", "", "", "0,10");

            if (response?.success) {
                const claimRows = normaliseRows(response);
                const filteredClaims = claimRows.filter((claim) => recordMatchesInsuranceUser(claim, userData, "claim"));

                const formatted = filteredClaims.slice(0, 5).map((claim) => {
                    let documents = claim.documents;
                    if (typeof documents === "string") {
                        try {
                            documents = JSON.parse(documents);
                        } catch (err) {
                            documents = [];
                        }
                    }

                    return {
                        id: claim.id,
                        claim_number: claim.claim_number || `CLM-${claim.id}`,
                        policy_number: claim.policy_number || claim.policy_id || "N/A",
                        claim_type: claim.claim_type || claim.claim_reason || "General",
                        claim_amount: claim.claim_amount ?? 0,
                        status: claim.status || "pending",
                        created_at: claim.created_at,
                        documents,
                    };
                });

                setRecentClaims(formatted);
            } else {
                setRecentClaims([]);
            }
        } catch (error) {
            console.error("Error fetching recent claims:", error);
            setRecentClaims([]);
        } finally {
            setLoadingClaims(false);
        }
    };

    useEffect(() => {
        // First, check if userData already has user_type
        if (userData?.user_type) {
            const userRole = userData.user_type.toString().toLowerCase().trim();
            setRole(userRole);
            if (userRole === "user" || userRole === "regular_users" || userRole === "regular users" || userRole === "patient") {
                fetchAppointmentCounts();
            }
            if (userRole.includes("insurance")) {
                fetchRecentClaims();
                fetchInsuranceSummary();
            }
        }

        // Then fetch from server to get complete user data
        const fetchUserRole = async () => {
            if (!userData?.id) return;
            try {
                const res = await oneKlicks.read("", "users", { id: userData.id }, "id ASC", "", "", "");
                if (res?.success && res?.data?.length > 0) {
                    const serverUser = res.data[0];
                    const normalizeRole = (r) => (r ? r.toString().toLowerCase().trim() : "");
                    const isUserRole = (r) => ["user", "regular_users", "regular users", "patient"].includes(r);

                    const serverRole = normalizeRole(serverUser.user_type);
                    const localRole = normalizeRole(userData?.user_type);

                    // Prefer a valid local user role if server is empty or mismatched
                    let resolvedRole = serverRole || localRole || role;
                    if (isUserRole(localRole) && !serverRole) {
                        resolvedRole = localRole;
                    }

                    if (resolvedRole) {
                        if (resolvedRole !== role) setRole(resolvedRole);
                        setMetrics((prev) => ({ ...prev, ...serverUser }));
                        if (isUserRole(resolvedRole)) {
                            fetchAppointmentCounts();
                            fetchRecentAppointments();
                        }
                        if (resolvedRole.includes("insurance")) {
                            fetchRecentClaims();
                            fetchInsuranceSummary();
                        }
                    }
                }
            } catch (e) {
                console.error("Error fetching user role:", e);
            }
        };
        fetchUserRole();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData?.id, userData?.user_type]);

    useEffect(() => {
        const userRole = role.toLowerCase().trim();
        const isUserType = userRole === "user" || userRole === "regular_users" || userRole === "patient" || userRole === "regular users" || userRole === "doctor";
        if (isUserType && userData?.id) {
            fetchRecentAppointments();
            fetchAppointmentCounts();
        }
        if (role.toLowerCase().includes("insurance") && userData?.id) {
            fetchInsuranceSummary();
        }
    }, [role, userData?.id]);

    const isInsurance = useMemo(() => {
        if (!role) return false;
        const userRole = role.toLowerCase();
        return userRole.includes("insurance");
    }, [role]);

    const isUser = useMemo(() => {
        if (!role) return false;
        const userRole = role.toLowerCase().trim();
        return ["user", "regular_users", "regular users", "patient"].includes(userRole);
    }, [role]);

    const isDoctor = useMemo(() => {
        if (!role) return false;
        const userRole = role.toLowerCase().trim();
        return userRole === "doctor";
    }, [role]);

    const renderDoctorCards = () => (
        <>
            {/* Appointment Summary Cards for Doctor */}
            <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                <div className="dc-insightsitem dc-dashboardbox">
                    <span><em className="dcunread-count">{appointmentCounts.total}</em></span>
                    <figure className="dc-userlistingimg">
                        <img src="//css.meddylist.com//wp-content/uploads/2019/10/invoice.png" alt="Total Appointments" />
                    </figure>
                    <div className="dc-insightdetails">
                        <div className="dc-title">
                            <h3>Total Appointments</h3>
                            <Link to="/doctor/appointmentlist">View All Appointments</Link>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                <div className="dc-insightsitem dc-dashboardbox">
                    <span><em className="dcunread-count">{appointmentCounts.upcoming}</em></span>
                    <figure className="dc-userlistingimg">
                        <img src="//css.meddylist.com//wp-content/uploads/2019/08/img-21.png" alt="Upcoming" />
                    </figure>
                    <div className="dc-insightdetails">
                        <div className="dc-title">
                            <h3>Upcoming</h3>
                            <Link to="/doctor/appointmentlist">View Upcoming</Link>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                <div className="dc-insightsitem dc-dashboardbox">
                    <span><em className="dcunread-count">{appointmentCounts.completed}</em></span>
                    <figure className="dc-userlistingimg">
                        <img src="//css.meddylist.com//wp-content/uploads/2019/10/list.png" alt="Completed" />
                    </figure>
                    <div className="dc-insightdetails">
                        <div className="dc-title">
                            <h3>Completed</h3>
                            <Link to="/doctor/appointmentlist">View Completed</Link>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                <div className="dc-insightsitem dc-dashboardbox">
                    <span><em className="dcunread-count">{appointmentCounts.cancelled}</em></span>
                    <figure className="dc-userlistingimg">
                        <img src="//css.meddylist.com//wp-content/uploads/2019/08/img-22.png" alt="Cancelled" />
                    </figure>
                    <div className="dc-insightdetails">
                        <div className="dc-title">
                            <h3>Cancelled</h3>
                            <Link to="/doctor/appointmentlist">View Cancelled</Link>
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                <div className="dc-insightsitem dc-dashboardbox">
                    <span><em className="dcunread-count">{appointmentCounts.recentPayments}</em></span>
                    <figure className="dc-userlistingimg">
                        <img src="//css.meddylist.com//wp-content/uploads/2019/10/invoice.png" alt="Recent Payments" />
                    </figure>
                    <div className="dc-insightdetails">
                        <div className="dc-title">
                            <h3>Recent Payments</h3>
                            <span style={{ fontSize: '12px', color: '#666' }}>Transactions Found</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Total Amount Card (Moved to end) */}
            <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                <div className="dc-insightsitem dc-dashboardbox">
                    <span><em className="dcunread-count" style={{ backgroundColor: '#ff5851' }}>{appointmentCounts.amount}</em></span>
                    <figure className="dc-userlistingimg">
                        <img src="//css.meddylist.com//wp-content/uploads/2019/10/invoice.png" alt="Total Revenue" />
                    </figure>
                    <div className="dc-insightdetails">
                        <div className="dc-title">
                            <h3>Total Revenue</h3>
                            <span style={{ fontSize: '12px', color: '#666' }}>Lifetime Earnings</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Existing Doctor Cards */}
            <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                <div className="dc-insightsitem dc-dashboardbox">
                    <span><em className="dcunread-count">{metrics.new_messages || 0}</em></span>
                    <figure className="dc-userlistingimg">
                        <img src="//css.meddylist.com//wp-content/uploads/2025/12/5356190.png" alt="New Messages" />
                    </figure>
                    <div className="dc-insightdetails">
                        <div className="dc-title">
                            <h3>New Messages</h3>
                            <Link to="/doctor/inbox">Click To View</Link>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                <div className="dc-insightsitem dc-dashboardbox">
                    <figure className="dc-userlistingimg">
                        <img src="//css.meddylist.com//wp-content/uploads/2019/10/list.png" alt="Add Article" />
                    </figure>
                    <div className="dc-insightdetails">
                        <div className="dc-title">
                            <h3>Add Article</h3>
                            <Link to="/doctor/managarticle">Click To View</Link>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                <div className="dc-insightsitem dc-dashboardbox">
                    <figure className="dc-userlistingimg">
                        <img src="//css.meddylist.com//wp-content/uploads/2025/12/balance-10.png" alt="Available Balance" />
                    </figure>
                    <div className="dc-insightdetails">
                        <div className="dc-title">
                            <h3>{metrics.available_balance || 0}</h3>
                            <span>Available balance</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                <div className="dc-insightsitem dc-dashboardbox">
                    <figure className="dc-userlistingimg">
                        <img src="//css.meddylist.com//wp-content/uploads/2019/10/support.png" alt="Specialties and Services" />
                    </figure>
                    <div className="dc-insightdetails">
                        <div className="dc-title">
                            <h3>Specialties and Services</h3>
                            <Link to="/doctor/specialitiesservices">Specialties and Services</Link>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    const renderInsuranceCards = () => {
        const cards = [
            {
                key: "insurance_policies_total",
                title: "Total Policies",
                badge: insuranceSummary.totalPolicies,
                description: `${insuranceSummary.activePolicies} active policies`,
                image: "//css.meddylist.com//wp-content/uploads/2019/08/img-22.png",
                link: metrics.policies_link || "/insurance/policies",
                linkLabel: "View Policies",
            },
            {
                key: "insurance_customers_total",
                title: "Customers",
                badge: insuranceSummary.totalCustomers,
                description: "Managed insurance customers",
                image: "//css.meddylist.com//wp-content/uploads/2019/10/invoice.png",
                link: "/insurance/customers",
                linkLabel: "Manage Customers",
            },
            {
                key: "insurance_leads_total",
                title: "Sales Leads",
                badge: insuranceSummary.totalLeads,
                description: `${insuranceSummary.openLeads} active • ${insuranceSummary.hotLeads} hot`,
                icon: "ti-target",
                link: "/insurance/leads",
                linkLabel: "Manage Leads",
            },
            {
                key: "insurance_beneficiaries_total",
                title: "Beneficiaries",
                badge: insuranceSummary.totalBeneficiaries,
                description: "Linked family members",
                image: "//css.meddylist.com//wp-content/uploads/2019/10/support.png",
                link: "/insurance/beneficiaries/new",
                linkLabel: "Add Beneficiary",
            },
            {
                key: "insurance_companies_total",
                title: "Partner Companies",
                badge: insuranceSummary.partnerCompanies,
                description: "Active insurance partners",
                icon: "ti-briefcase",
                link: "/insurance/companies",
                linkLabel: "Company Directory",
            },
            {
                key: "insurance_agents_total",
                title: "Agents",
                badge: insuranceSummary.totalAgents,
                description: "Agents & advisors",
                icon: "ti-id-badge",
                link: "/insurance/agents/new",
                linkLabel: "Add Agent",
            },
            {
                key: "insurance_premium_total",
                title: "Premium Collected",
                badge: formatCurrency(insuranceSummary.premiumCollected),
                description: `Avg ${formatCurrency(Math.round(insuranceSummary.averagePremium || 0))} per policy`,
                image: "//css.meddylist.com//wp-content/uploads/2025/02/balance-10.png",
                link: "/insurance/analytics",
                linkLabel: "Analytics",
            },
            {
                key: "insurance_claim_assistance",
                title: "Claim Assistance",
                badge: recentClaims.length,
                description: "Open assistance requests",
                image: "//css.meddylist.com//wp-content/uploads/2019/10/support.png",
                link: "/insurance/help",
                linkLabel: "Assist Customers",
            },
            {
                key: "insurance_emergency_support",
                title: "Emergency Support",
                badge: metrics.emergency_contacts_count || 1,
                description: "Emergency hotline & contacts",
                icon: "ti-headphone",
                link: metrics.emergency_support_link || "/insurance/help",
                linkLabel: "Open Support Desk",
            },
            {
                key: "insurance_documents_total",
                title: "Documents Vault",
                badge: metrics.documents_total || 0,
                description: "Policies & compliance files",
                image: "//css.meddylist.com//wp-content/uploads/2019/10/list.png",
                link: "/insurance/documents",
                linkLabel: "Manage Documents",
            },
        ];

        const containerStyle = {
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "space-between",
            minHeight: "220px",
            padding: "24px 24px 20px",
        };

        const titleStyle = {
            display: "flex",
            flexDirection: "column",
            gap: "6px",
        };

        return (
            <>
                {cards.map((card) => (
                    <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3" key={card.key}>
                        <div className="dc-insightsitem dc-dashboardbox" style={containerStyle}>
                            <span><em className="dcunread-count">{card.badge}</em></span>
                            <figure className="dc-userlistingimg" style={{ marginBottom: "16px" }}>
                                {card.icon ? (
                                    <span className={card.icon} style={{ fontSize: "32px", color: "var(--themecolor)" }}></span>
                                ) : (
                                    <img src={card.image} alt={card.title} />
                                )}
                            </figure>
                            <div className="dc-insightdetails" style={{ flex: 1 }}>
                                <div className="dc-title" style={titleStyle}>
                                    <h3>{card.title}</h3>
                                    <span style={{ color: "#666", fontSize: "13px" }}>{card.description}</span>
                                    {card.link && (
                                        <Link to={card.link} style={{ marginTop: "4px", display: "inline-block" }}>
                                            {card.linkLabel || "Manage"}
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </>
        );
    };

    const renderUserCards = () => (
        <>
            {/* Appointment Summary Cards */}
            <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                <div className="dc-insightsitem dc-dashboardbox">
                    <span><em className="dcunread-count">{appointmentCounts.total}</em></span>
                    <figure className="dc-userlistingimg">
                        <img src="//css.meddylist.com//wp-content/uploads/2019/10/invoice.png" alt="Total Appointments" />
                    </figure>
                    <div className="dc-insightdetails">
                        <div className="dc-title">
                            <h3>Total Appointments</h3>
                            <Link to={metrics.all_appointments_link || "/user-appointments"}>View All Appointments</Link>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                <div className="dc-insightsitem dc-dashboardbox">
                    <span><em className="dcunread-count">{appointmentCounts.upcoming}</em></span>
                    <figure className="dc-userlistingimg">
                        <img src="//css.meddylist.com//wp-content/uploads/2019/08/img-21.png" alt="Upcoming" />
                    </figure>
                    <div className="dc-insightdetails">
                        <div className="dc-title">
                            <h3>Upcoming</h3>
                            <Link to={metrics.upcoming_appointments_link || "/user-appointments?status=upcoming"}>View Upcoming</Link>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                <div className="dc-insightsitem dc-dashboardbox">
                    <span><em className="dcunread-count">{appointmentCounts.completed}</em></span>
                    <figure className="dc-userlistingimg">
                        <img src="//css.meddylist.com//wp-content/uploads/2019/10/list.png" alt="Completed" />
                    </figure>
                    <div className="dc-insightdetails">
                        <div className="dc-title">
                            <h3>Completed</h3>
                            <Link to={metrics.completed_appointments_link || "/user-appointments?status=completed"}>View Completed</Link>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                <div className="dc-insightsitem dc-dashboardbox">
                    <span><em className="dcunread-count">{appointmentCounts.cancelled}</em></span>
                    <figure className="dc-userlistingimg">
                        <img src="//css.meddylist.com//wp-content/uploads/2019/08/img-22.png" alt="Cancelled" />
                    </figure>
                    <div className="dc-insightdetails">
                        <div className="dc-title">
                            <h3>Cancelled</h3>
                            <Link to={metrics.cancelled_appointments_link || "/user-appointments?status=cancelled"}>View Cancelled</Link>
                        </div>
                    </div>
                </div>
            </div>
            {/* Quick Actions Cards */}
            <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                <div className="dc-insightsitem dc-dashboardbox">
                    <figure className="dc-userlistingimg">
                        <img src="//css.meddylist.com//wp-content/uploads/2019/08/img-21.png" alt="Book New Appointment" />
                    </figure>
                    <div className="dc-insightdetails">
                        <div className="dc-title">
                            <h3>Book New Appointment</h3>
                            <span>Schedule your next health check</span>
                            <Link to={metrics.book_appointment_link || "/search-doctors"}>Book Now</Link>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                <div className="dc-insightsitem dc-dashboardbox">
                    <figure className="dc-userlistingimg">
                        <img src="//css.meddylist.com//wp-content/uploads/2019/10/support.png" alt="Find Doctors" />
                    </figure>
                    <div className="dc-insightdetails">
                        <div className="dc-title">
                            <h3>Find Doctors</h3>
                            <span>Browse specialist doctors</span>
                            <Link to={metrics.find_doctors_link || "/search-doctors"}>Search Doctors</Link>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                <div className="dc-insightsitem dc-dashboardbox">
                    <figure className="dc-userlistingimg">
                        <img src="//css.meddylist.com//wp-content/uploads/2019/10/invoice.png" alt="View Medical Records" />
                    </figure>
                    <div className="dc-insightdetails">
                        <div className="dc-title">
                            <h3>View Medical Records</h3>
                            <span>Access your health history</span>
                            <Link to={metrics.medical_records_link || "/medical-records"}>View Records</Link>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                <div className="dc-insightsitem dc-dashboardbox">
                    <figure className="dc-userlistingimg">
                        <img src="//css.meddylist.com//wp-content/uploads/2019/10/support.png" alt="Emergency Contact" />
                    </figure>
                    <div className="dc-insightdetails">
                        <div className="dc-title">
                            <h3>Emergency Contact</h3>
                            <span>Quick emergency access</span>
                            <Link to={metrics.emergency_contact_link || "/emergency"}>Emergency</Link>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return "N/A";
            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        } catch (error) { return "N/A"; }
    };

    const getDisplayName = (apt) => {
        if (isDoctor) {
            const patient = apt.patient?.[0] || {};
            if (patient.display_name) return patient.display_name;
            if (patient.first_name || patient.last_name) return `${patient.first_name || ''} ${patient.last_name || ''}`.trim();
            if (apt.patient_name) return apt.patient_name;
            return "Unknown Patient";
        } else {
            if (apt.doctor?.[0]?.display_name) return apt.doctor[0].display_name;
            if (apt.doctor_name) return apt.doctor_name;
            if (apt.hospital_name) return apt.hospital_name;
            return "Healthcare Provider";
        }
    };

    const getStatusBadge = (status) => {
        const statusLower = (status || "").toLowerCase();
        let variant = "secondary";
        if (statusLower === "confirmed" || statusLower === "upcoming") variant = "success";
        else if (statusLower === "pending") variant = "warning";
        else if (statusLower === "completed" || statusLower === "done") variant = "info";
        else if (statusLower === "cancelled" || statusLower === "canceled") variant = "danger";

        return <Badge bg={variant} style={{ borderRadius: '4px', padding: '5px 10px', fontSize: '11px', fontWeight: '600' }}>{status || "Unknown"}</Badge>;
    };

    const getImageUrl = (apt) => {
        if (isDoctor) {
            const patient = apt.patient?.[0] || {};
            // If profile_photo_url is missing or a placeholder string, return null to let ImageShow use its internal placeholder
            if (!patient.profile_photo_url || patient.profile_photo_url === "/default-avatar.png") return null;
            return patient.profile_photo_url;
        } else {
            if (apt.doctor?.[0]?.profile_photo_url) return apt.doctor[0].profile_photo_url;
            if (apt.doctor_photo) return apt.doctor_photo;
            return null;
        }
    };

    // Determine which cards to render
    const renderCards = () => {
        if (isInsurance) {
            return renderInsuranceCards();
        } else if (isUser) {
            return renderUserCards();
        } else if (role) {
            // Only show doctor cards if role is set and not empty
            return renderDoctorCards();
        }
        // Return null while loading
        return null;
    };

    return (
        <section className="dc-haslayout dc-dbsectionspace">
            <div className="row">
                <div className="dc-haslayout dc-jobpostedholder">
                    {renderCards()}
                </div>
                <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12 col-xl-12">
                    <style>
                        {`
                            @media (max-width: 767px) {
                                .dc-table-responsive thead {
                                    display: none;
                                }
                                .dc-table-responsive tbody tr {
                                    display: block;
                                    margin-bottom: 20px;
                                    border: 1px solid #eee;
                                    border-radius: 10px;
                                    background: #fff;
                                    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                                    padding: 10px;
                                }
                                .dc-table-responsive tbody td {
                                    display: flex;
                                    justify-content: space-between;
                                    align-items: center;
                                    padding: 12px 15px !important;
                                    border-bottom: 1px solid #f5f5f5;
                                    text-align: right;
                                    font-size: 13px;
                                    min-height: 45px;
                                }
                                .dc-table-responsive tbody td:last-child {
                                    border-bottom: 0;
                                    margin-top: 10px;
                                }
                                .dc-table-responsive tbody td::before {
                                    content: attr(data-label);
                                    font-weight: 700;
                                    color: #666;
                                    text-transform: uppercase;
                                    font-size: 10px;
                                    letter-spacing: 0.5px;
                                    text-align: left;
                                    flex: 1;
                                }
                                .dc-table-responsive .d-flex.align-items-center {
                                    justify-content: flex-end;
                                    width: 100%;
                                }
                            }
                        `}
                    </style>
                    <div className="dc-dashboardbox dc-dashboardtabsholder">
                        <div className="dc-dashboardboxtitle"><h2>{isInsurance ? (metrics.recent_claims_title || "Recent Activity") : isUser ? (metrics.recent_appointments_title || "Recent Appointments") : (metrics.recent_appointments_title || "Recent Appointments")}</h2></div>
                        {isInsurance ? (
                            loadingInsuranceActivity ? (
                                <div className="dc-emptydata-holder">
                                    <div className="dc-emptydata">
                                        <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                                            <span></span>
                                            <em>Loading activities...</em>
                                        </div>
                                    </div>
                                </div>
                            ) : recentInsuranceActivities.length > 0 ? (
                                <div className="dc-dashboardboxcontent p-0">
                                    <div className="table-responsive">
                                        <table className="table dc-table-responsive mb-0 align-middle">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Activity</th>
                                                    <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Details</th>
                                                    <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666' }}>When</th>
                                                    <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666', textAlign: 'right' }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recentInsuranceActivities.map((activity, index) => {
                                                    const meta = insuranceActivityMeta[activity.type] || insuranceActivityMeta.default;
                                                    return (
                                                        <tr key={`${activity.type}-${index}`}>
                                                            <td data-label="Activity" style={{ padding: '15px 20px' }}>
                                                                <div className="d-flex align-items-center gap-3">
                                                                    <span
                                                                        style={{
                                                                            width: "36px",
                                                                            height: "36px",
                                                                            borderRadius: "8px",
                                                                            backgroundColor: `${meta.color}15`,
                                                                            color: meta.color,
                                                                            display: "inline-flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            fontSize: "16px",
                                                                            flexShrink: 0
                                                                        }}
                                                                    >
                                                                        <i className={meta.icon}></i>
                                                                    </span>
                                                                    <div style={{ fontWeight: "600", color: "#333", fontSize: "13px" }}>{activity.title}</div>
                                                                </div>
                                                            </td>
                                                            <td data-label="Details" style={{ padding: '15px 20px' }}>
                                                                <div style={{ fontSize: '13px', color: '#666' }}>{activity.description || "—"}</div>
                                                            </td>
                                                            <td data-label="When" style={{ padding: '15px 20px' }}>
                                                                <div style={{ fontSize: "12px", color: "#999", fontWeight: "500" }}>{formatTimeAgo(activity.timestamp)}</div>
                                                            </td>
                                                            <td data-label="Action" style={{ padding: '15px 20px', textAlign: 'right' }}>
                                                                {activity.link ? (
                                                                    <Link
                                                                        to={activity.link}
                                                                        className="btn btn-sm"
                                                                        style={{
                                                                            backgroundColor: "transparent",
                                                                            color: "var(--themecolor)",
                                                                            border: "1px solid var(--themecolor)",
                                                                            padding: "4px 12px",
                                                                            borderRadius: "4px",
                                                                            fontSize: "12px",
                                                                            fontWeight: "600"
                                                                        }}
                                                                    >
                                                                        View
                                                                    </Link>
                                                                ) : (
                                                                    <span style={{ color: "#ccc" }}>—</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                <div className="dc-emptydata-holder">
                                    <div className="dc-emptydata">
                                        <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                                            <span></span>
                                            <em>{metrics.recent_claims_empty_message || "There are no activities."}</em>
                                        </div>
                                    </div>
                                </div>
                            )
                        ) : loadingAppointments ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem', borderColor: 'var(--themecolor)', borderRightColor: 'transparent' }}>
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2 text-muted">Loading appointments...</p>
                            </div>
                        ) : recentAppointments.length > 0 ? (
                            <div className="dc-dashboardboxcontent p-0">
                                <div className="table-responsive">
                                    <table className="table mb-0 align-middle dc-table-responsive">
                                        <thead className="bg-light">
                                            <tr>
                                                <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666' }}>ID</th>
                                                <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666' }}>{isDoctor ? 'User / Patient Name' : 'Doctor / Clinic'}</th>
                                                <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Schedule</th>
                                                <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Note</th>
                                                <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Status</th>
                                                <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666', textAlign: 'right' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentAppointments.map((appointment) => {
                                                return (
                                                    <tr key={appointment.id}>
                                                        <td data-label="ID" style={{ padding: '12px 20px', fontSize: '13px', color: '#999' }}>#{appointment.id}</td>
                                                        <td data-label={isDoctor ? "Patient" : "Doctor"} style={{ padding: '12px 20px' }}>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <ImageShow
                                                                    url={getImageUrl(appointment)}
                                                                    type={isDoctor ? "patient" : "doctor"}
                                                                    width={35}
                                                                    height={35}
                                                                    style={{
                                                                        objectFit: 'cover',
                                                                        borderRadius: '50%',
                                                                        border: '1px solid #eee'
                                                                    }}
                                                                />
                                                                <div className="text-end text-md-start">
                                                                    <div style={{ fontWeight: '600', color: '#333', fontSize: '13px', lineHeight: '1.2' }}>{getDisplayName(appointment)}</div>
                                                                    <div style={{ fontSize: '11px', color: '#999' }}>{isDoctor ? "Patient" : "Healthcare Provider"}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td data-label="Schedule" style={{ padding: '12px 20px' }}>
                                                            <div className="text-end">
                                                                <div style={{ fontSize: '13px', fontWeight: '500' }}>{formatDate(appointment.appointment_date)}</div>
                                                                <div style={{ fontSize: '11px', color: '#999' }}>{appointment.appointment_time || "N/A"}</div>
                                                            </div>
                                                        </td>
                                                        <td data-label="Note" style={{ padding: '12px 20px' }}>
                                                            <div className="text-truncate" style={{ maxWidth: '150px', fontSize: '13px', color: '#666' }} title={appointment.note || "General Checkup"}>
                                                                {appointment.note || "General Checkup"}
                                                            </div>
                                                        </td>
                                                        <td data-label="Status" style={{ padding: '12px 20px' }}>{getStatusBadge(appointment.status)}</td>
                                                        <td data-label="Action" style={{ padding: '12px 20px', textAlign: 'right' }}>
                                                            <Link to={isUser ? `/user-appointments?id=${appointment.id}` : `/doctor/appointmentlist`} className="btn btn-sm btn-primary" style={{ backgroundColor: 'var(--themecolor)', borderColor: 'var(--themecolor)', borderRadius: '4px', fontSize: '12px', padding: '4px 12px' }}>
                                                                View
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="p-4 text-right" style={{ borderTop: '1px solid #eee', background: '#fcfcfc' }}>
                                    <Link
                                        to={isUser ? "/user-appointments" : "/doctor/appointmentlist"}
                                        className="btn dc-btn-view-all"
                                        style={{
                                            backgroundColor: 'var(--themecolor)',
                                            color: '#fff',
                                            padding: '12px 35px',
                                            borderRadius: '5px',
                                            fontWeight: '700',
                                            fontSize: '14px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '1px',
                                            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            border: '2px solid var(--themecolor)',
                                            textDecoration: 'none'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#fff';
                                            e.currentTarget.style.color = 'var(--themecolor)';
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'var(--themecolor)';
                                            e.currentTarget.style.color = '#fff';
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                                        }}
                                    >
                                        <span>View All Appointments</span>
                                        <i className="ti-arrow-right" style={{ fontSize: '16px' }}></i>
                                    </Link>
                                </div>
                            </div>
                        ) : (
                            <div className="dc-emptydata-holder">
                                <div className="dc-emptydata">
                                    <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                                        <span></span>
                                        <em>{isInsurance ? (metrics.recent_claims_empty_message || "There are no activities.") : isUser ? (metrics.recent_appointments_empty_message || "There are no appointments.") : (metrics.recent_appointments_empty_message || "There are no appointments.")}</em>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {isDoctor && (
                        <div className="dc-dashboardbox dc-dashboardtabsholder mt-4">
                            <div className="dc-dashboardboxtitle" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h2>Recent Payments (Revenue)</h2>
                                <span style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>Total: {formatCurrency(appointmentCounts.totalRevenue)}</span>
                            </div>
                            <div className="dc-dashboardboxcontent p-0">
                                <div className="table-responsive">
                                    <table className="table mb-0 align-middle dc-table-responsive">
                                        <thead className="bg-light">
                                            <tr>
                                                <th style={{ padding: '15px 20px', fontSize: '13px', fontWeight: '700', color: '#444' }}>Transaction ID</th>
                                                <th style={{ padding: '15px 20px', fontSize: '13px', fontWeight: '700', color: '#444' }}>User / Patient</th>
                                                <th style={{ padding: '15px 20px', fontSize: '13px', fontWeight: '700', color: '#444' }}>Date</th>
                                                <th style={{ padding: '15px 20px', fontSize: '13px', fontWeight: '700', color: '#444' }}>Method</th>
                                                <th style={{ padding: '15px 20px', fontSize: '13px', fontWeight: '700', color: '#444' }}>Amount</th>
                                                <th style={{ padding: '15px 20px', fontSize: '13px', fontWeight: '700', color: '#444', textAlign: 'right' }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentAppointments.filter(apt => (apt.status || "").toLowerCase() === "completed" || (apt.status || "").toLowerCase() === "done" || Number(apt.amount) > 0).length > 0 ? (
                                                recentAppointments
                                                    .filter(apt => (apt.status || "").toLowerCase() === "completed" || (apt.status || "").toLowerCase() === "done" || Number(apt.amount) > 0)
                                                    .map((apt) => (
                                                        <tr key={`payment-${apt.id}`}>
                                                            <td data-label="Trans ID" style={{ padding: '15px 20px', fontSize: '12px', color: '#999', fontFamily: 'monospace' }}>
                                                                {apt.appointment_id || `TXN-${apt.id}`}
                                                            </td>
                                                            <td data-label="Patient" style={{ padding: '15px 20px' }}>
                                                                <div style={{ fontWeight: '600', color: '#333' }}>
                                                                    {apt.patient?.[0]?.first_name || apt.patient?.[0]?.display_name || apt.patient_name || "Patient"}
                                                                </div>
                                                                <div style={{ fontSize: '11px', color: '#999' }}>ID: #{apt.patient_id}</div>
                                                            </td>
                                                            <td data-label="Date" style={{ padding: '15px 20px' }}>
                                                                <div style={{ fontSize: '13px', fontWeight: '500' }}>{new Date(apt.appointment_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</div>
                                                                <div style={{ fontSize: '11px', color: '#999' }}>{apt.appointment_time}</div>
                                                            </td>
                                                            <td data-label="Method" style={{ padding: '15px 20px' }}>
                                                                <span style={{ fontSize: '12px', color: '#666', textTransform: 'capitalize' }}>
                                                                    {apt.payment_mode || 'Online'}
                                                                </span>
                                                            </td>
                                                            <td data-label="Amount" style={{ padding: '15px 20px' }}>
                                                                <div style={{ color: '#059669', fontWeight: '700', fontSize: '15px' }}>
                                                                    {formatCurrency(apt.amount || apt.display_amount || 0)}
                                                                </div>
                                                                {apt.transaction_fee > 0 && (
                                                                    <div style={{ fontSize: '10px', color: '#999' }}>Inc. {formatCurrency(apt.transaction_fee)} fee</div>
                                                                )}
                                                            </td>
                                                            <td data-label="Status" style={{ padding: '15px 20px', textAlign: 'right' }}>
                                                                <Badge bg={apt.payment_status === 'pending' ? 'warning' : 'success'} style={{ borderRadius: '4px', textTransform: 'uppercase', fontSize: '10px' }}>
                                                                    {apt.payment_status || 'Paid'}
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-5 text-muted">
                                                        <i className="ti-receipt" style={{ fontSize: '32px', display: 'block', marginBottom: '10px', opacity: 0.3 }}></i>
                                                        No payment records found for your appointments yet.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Appointment Detail Modal */}
                <Modal
                    show={showAppointmentDetailModal}
                    onHide={() => setShowAppointmentDetailModal(false)}
                    centered
                    size="lg"
                    className="dc-appointment-modal"
                >
                    <Modal.Header closeButton style={{ borderBottom: '1px solid #eee', padding: '20px 30px' }}>
                        <Modal.Title style={{ fontSize: '18px', fontWeight: '700', color: '#333' }}>
                            Appointment Details <span style={{ color: '#999', fontSize: '14px', fontWeight: '400' }}>#{selectedAppointment?.id}</span>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: '30px' }}>
                        {selectedAppointment && (
                            <div className="row g-4">
                                <div className="col-md-6">
                                    <div className="p-4" style={{ background: '#f8fafc', borderRadius: '15px' }}>
                                        <h5 style={{ fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', marginBottom: '20px', fontWeight: '700' }}>Patient Information</h5>
                                        <div className="d-flex align-items-center gap-3 mb-4">
                                            <ImageShow
                                                url={getImageUrl(selectedAppointment)}
                                                type="patient"
                                                width={60}
                                                height={60}
                                                style={{ borderRadius: '12px', border: '2px solid #fff', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                                            />
                                            <div>
                                                <div style={{ fontSize: '18px', fontWeight: '800', color: '#1e293b' }}>{getDisplayName(selectedAppointment)}</div>
                                                <div style={{ fontSize: '13px', color: '#64748b' }}>ID: #{selectedAppointment.patient_id}</div>
                                            </div>
                                        </div>
                                        <div className="space-y-3">
                                            <div className="d-flex justify-content-between">
                                                <span style={{ color: '#64748b', fontSize: '13px' }}>Patient Name</span>
                                                <span style={{ color: '#1e293b', fontWeight: '600', fontSize: '13px' }}>{selectedAppointment.patient?.[0]?.first_name || selectedAppointment.patient_name || '--'}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mt-2">
                                                <span style={{ color: '#64748b', fontSize: '13px' }}>Contact</span>
                                                <span style={{ color: '#1e293b', fontWeight: '600', fontSize: '13px' }}>{selectedAppointment.patient?.[0]?.phone || selectedAppointment.patient_phone || '--'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="p-4" style={{ background: '#f8fafc', borderRadius: '15px' }}>
                                        <h5 style={{ fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', marginBottom: '20px', fontWeight: '700' }}>Schedule Details</h5>
                                        <div className="mb-4">
                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                <i className="ti-calendar text-primary"></i>
                                                <span style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>{formatDate(selectedAppointment.appointment_date)}</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <i className="ti-time text-primary"></i>
                                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b' }}>{selectedAppointment.appointment_time}</span>
                                            </div>
                                        </div>
                                        <div className="space-y-3">
                                            <div className="d-flex justify-content-between">
                                                <span style={{ color: '#64748b', fontSize: '13px' }}>Status</span>
                                                {getStatusBadge(selectedAppointment.status)}
                                            </div>
                                            <div className="d-flex justify-content-between mt-3">
                                                <span style={{ color: '#64748b', fontSize: '13px' }}>Payment</span>
                                                <Badge bg={selectedAppointment.payment_status === 'paid' ? 'success' : 'warning'} style={{ borderRadius: '4px', textTransform: 'uppercase', fontSize: '10px' }}>
                                                    {selectedAppointment.payment_status || 'Pending'}
                                                </Badge>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="p-4" style={{ border: '1px dashed #e2e8f0', borderRadius: '15px' }}>
                                        <h5 style={{ fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', marginBottom: '10px', fontWeight: '700' }}>Reason / Note</h5>
                                        <p style={{ color: '#475569', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                                            {selectedAppointment.note || "No specific note provided for this appointment."}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer style={{ borderTop: 'none', padding: '20px 30px' }}>
                        <Button variant="outline-secondary" onClick={() => setShowAppointmentDetailModal(false)} style={{ borderRadius: '8px', padding: '10px 25px', fontSize: '14px', fontWeight: '600' }}>
                            Close
                        </Button>
                        <Button variant="primary" style={{ backgroundColor: 'var(--themecolor)', borderColor: 'var(--themecolor)', borderRadius: '8px', padding: '10px 25px', fontSize: '14px', fontWeight: '600' }}>
                            Update Status
                        </Button>
                    </Modal.Footer>
                </Modal>
                <div className="dc-uploadimages dc-package-modal modal fade" id="dc-package-details" tabIndex="-1" role="dialog" aria-hidden="true">
                    <div className="dc-modaldialog modal-dialog modal-dialog-centered" role="document">
                        <div className="dc-modalcontent modal-content">
                            <div className="dc-boxtitle">
                                <h2>{metrics.package_details_title || "Standard"}<a href="javascript:;" dataDismiss="modal" aria-label="Close"><i className="dc-btncancel fa fa-times"></i></a></h2>
                            </div>
                            <div className="dc-modalbody modal-body">
                                <div className="dc-dashboardboxcontent dc-packages">
                                    <div className="dc-package dc-packagedetails">
                                        <div className="dc-packagecontent">
                                            <ul className="dc-packageinfo">
                                                <li><span>No of services per speciality</span></li>
                                                <li><span>No of Brochures</span></li>
                                                <li><span>No of Articles</span></li>
                                                <li><span>No of Awards</span></li>
                                                <li><span>No of Memberships</span></li>
                                                <li><span>Private Quick Chat</span></li>
                                                <li><span>Bookings</span></li>
                                                <li><span>Featured Profile?</span></li>
                                                <li><span>Featured days?</span></li>
                                                <li><span>Duration</span></li>
                                                <li><span>No Social Model Post</span></li>
                                                <li><span>No Social Model Videos</span></li>
                                                <li><span>Auto Post ?</span></li>
                                                <li><span>Profile Website</span></li>
                                                <li><span>HMS</span></li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="dc-package dc-baiscpackage">
                                        <div className="dc-packagecontent">
                                            <ul className="dc-packageinfo">
                                                <li><span>15&nbsp;</span></li>
                                                <li><span>3&nbsp;</span></li>
                                                <li><span>5&nbsp;</span></li>
                                                <li><span>5&nbsp;</span></li>
                                                <li><span>5&nbsp;</span></li>
                                                <li><span><i className="ti-check"></i>&nbsp;</span></li>
                                                <li><span><i className="ti-check"></i>&nbsp;</span></li>
                                                <li><span><i className="ti-check"></i>&nbsp;</span></li>
                                                <li><span>100&nbsp;</span></li>
                                                <li><span>30&nbsp;</span></li>
                                                <li><span>30&nbsp;</span></li>
                                                <li><span>10&nbsp;</span></li>
                                                <li><span><i className="ti-check"></i>&nbsp;</span></li>
                                                <li><span><i className="ti-check"></i>&nbsp;</span></li>
                                                <li><span><i className="ti-check"></i>&nbsp;</span></li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CardViews;
